import { Switch, Typography } from '@mui/material';
import { QUERY_KEYS } from 'api/query-keys';
import { DateTime } from 'luxon';
import { accreditationsService } from 'ogp/services';
import React from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { Column } from 'react-table';
import { TableHeader } from 'redesign';
import { TFunctionNonStrict } from 'i18next';
import { CertificateCellProps, CertificateTableRow } from '../employee-certificates-tab.types';

export const getCertificatesColumnDefinition = (
  t: TFunctionNonStrict<'web-common', undefined>
): Column<CertificateTableRow>[] => {
  const certificateName: Column<CertificateTableRow> = {
    Header: () => (
      <TableHeader> {t('table_headers.certificate_name', { ns: 'web-common' })}</TableHeader>
    ),
    accessor: 'certificateName',
    Cell: CertificateNameCell,
  };

  const startDate: Column<CertificateTableRow> = {
    Header: () => <TableHeader> {t('start_date', { ns: 'web-common' })}</TableHeader>,
    accessor: 'startDate',
    width: 150,
    Cell: StartDateCell,
  };

  const endDate: Column<CertificateTableRow> = {
    Header: () => <TableHeader> {t('end_date', { ns: 'web-common' })}</TableHeader>,
    accessor: 'endDate',
    width: 150,
    Cell: EndDateCell,
  };

  const trust: Column<CertificateTableRow> = {
    Header: () => <TableHeader> {t('table_headers.trusted', { ns: 'web-common' })}</TableHeader>,
    accessor: 'trust',
    width: 80,
    Cell: TrustCell,
  };

  return [certificateName, startDate, endDate, trust];
};

const CertificateNameCell = (cell: CertificateCellProps<'certificateName'>) => {
  return (
    <Typography variant="body1" noWrap>
      {cell.value}
    </Typography>
  );
};

const StartDateCell = (cell: CertificateCellProps<'startDate'>) => {
  if (!cell.value) {
    return (
      <Typography variant="body1" color="secondary">
        -
      </Typography>
    );
  }

  const formatted = DateTime.fromISO(cell.value).toLocaleString(DateTime.DATE_MED, {
    locale: 'nl',
  });

  return (
    <Typography variant="body1" color="secondary">
      {formatted}
    </Typography>
  );
};

const EndDateCell = (cell: CertificateCellProps<'endDate'>) => {
  if (!cell.value) {
    return (
      <Typography variant="body1" color="secondary">
        -
      </Typography>
    );
  }

  const formatted = DateTime.fromISO(cell.value).toLocaleString(DateTime.DATE_MED, {
    locale: 'nl',
  });

  return (
    <Typography variant="body1" color="secondary">
      {formatted}
    </Typography>
  );
};

const TrustCell = ({ value, row }: CertificateCellProps<'trust'>) => {
  const { accreditationId, flexWorkerId } = row.original;
  const queryClient = useQueryClient();

  const trustMutation = useMutation(
    () => accreditationsService.trustAccreditation(flexWorkerId, accreditationId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(QUERY_KEYS.employeeCertificates(flexWorkerId));
      },
    }
  );

  const untrustMutation = useMutation(
    () => accreditationsService.untrustAccreditation(flexWorkerId, accreditationId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(QUERY_KEYS.employeeCertificates(flexWorkerId));
      },
    }
  );

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    event.stopPropagation();
    event.preventDefault();
    if (event.target.checked) {
      trustMutation.mutate();
    } else {
      untrustMutation.mutate();
    }
  };

  return (
    <Switch
      checked={value}
      onChange={handleChange}
      size="small"
      disabled={trustMutation.isLoading || untrustMutation.isLoading}
    />
  );
};
